// Responder un caso de WhatsApp (D73). Muestra cuanto queda de la ventana de 24 h: dentro, texto libre; fuera (o si la
// conversacion aun no existe), solo una plantilla aprobada en Meta, con sus variables.
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { enviarWhatsApp, plantillasWhatsApp, variablesDe } from '../api/whatsapp';
import { useNow } from '../hooks';
import { Icon } from '../lib/icons';
import { toast } from './toasts';

const DIA = 24 * 3600_000;

export function WhatsAppRespuesta({ casoId, ultimoCliente, conConversacion, alEnviar }: { casoId: string; ultimoCliente: string | null; conConversacion: boolean; alEnviar: () => void }) {
  const ahora = useNow(30_000);
  const quedan = ultimoCliente ? Date.parse(ultimoCliente) + DIA - ahora : 0;
  const abierta = conConversacion && quedan > 0;
  const [modo, setModo] = useState<'texto' | 'plantilla'>(abierta ? 'texto' : 'plantilla');
  const [texto, setTexto] = useState('');
  const [elegida, setElegida] = useState('');
  const [params, setParams] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);
  const plantillas = useQuery({ queryKey: ['whatsapp-plantillas'], queryFn: plantillasWhatsApp, enabled: modo === 'plantilla', staleTime: 10 * 60_000, retry: 1 });
  const p = plantillas.data?.find((x) => `${x.nombre}|${x.idioma}` === elegida);
  const n = p ? variablesDe(p.texto) : 0;
  const usarTexto = abierta && modo === 'texto';

  const enviar = async () => {
    setEnviando(true);
    try {
      if (usarTexto) await enviarWhatsApp({ casoId, texto: texto.trim() });
      else if (p) await enviarWhatsApp({ casoId, plantilla: { nombre: p.nombre, idioma: p.idioma, parametros: params.slice(0, n) } });
      setTexto('');
      setParams([]);
      toast('Enviado por WhatsApp');
      alEnviar();
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setEnviando(false);
    }
  };

  const h = Math.floor(quedan / 3600_000);
  const m = Math.floor((quedan % 3600_000) / 60_000);
  return (
    <section className="wa-resp" aria-label="Responder por WhatsApp">
      <div className="wa-h">
        <span className={`pill ${abierta ? (quedan < 2 * 3600_000 ? 'warn' : 'ok') : 'bad'}`}>
          <span className="pd" />
          {abierta ? `Ventana abierta: quedan ${h} h ${m} min` : conConversacion ? 'Pasaron 24 h: solo plantilla aprobada' : 'Sin conversación: empieza con una plantilla'}
        </span>
        {abierta ? (
          <span className="wa-modo" role="group" aria-label="Tipo de mensaje">
            <button type="button" className={`btn sm ${modo === 'texto' ? 'primary' : ''}`} onClick={() => setModo('texto')}>
              Texto
            </button>
            <button type="button" className={`btn sm ${modo === 'plantilla' ? 'primary' : ''}`} onClick={() => setModo('plantilla')}>
              Plantilla
            </button>
          </span>
        ) : null}
      </div>
      {usarTexto ? (
        <>
          <label htmlFor="wa-texto" className="sr-only">
            Mensaje de WhatsApp
          </label>
          <textarea id="wa-texto" rows={3} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Escribe la respuesta para el cliente…" />
        </>
      ) : (
        <div className="wa-plantilla">
          <label htmlFor="wa-plantilla">Plantilla aprobada</label>
          <select id="wa-plantilla" value={elegida} onChange={(e) => (setElegida(e.target.value), setParams([]))}>
            <option value="">{plantillas.isLoading ? 'Cargando…' : plantillas.error ? 'No se pudieron leer las plantillas' : 'Elige una plantilla'}</option>
            {(plantillas.data ?? []).map((x) => (
              <option key={`${x.nombre}|${x.idioma}`} value={`${x.nombre}|${x.idioma}`}>
                {x.nombre} ({x.idioma})
              </option>
            ))}
          </select>
          {p ? <p className="note">{p.texto}</p> : null}
          {Array.from({ length: n }, (_, i) => (
            <label key={i} htmlFor={`wa-var-${i}`}>
              Variable {`{{${i + 1}}}`}
              <input id={`wa-var-${i}`} value={params[i] ?? ''} onChange={(e) => setParams((v) => Object.assign([...v], { [i]: e.target.value }))} />
            </label>
          ))}
        </div>
      )}
      <div className="wa-acciones">
        <button className="btn primary" type="button" disabled={enviando || (usarTexto ? !texto.trim() : !p || params.slice(0, n).filter((x) => x?.trim()).length < n)} onClick={() => void enviar()}>
          <Icon name="send" /> {enviando ? 'Enviando…' : 'Enviar por WhatsApp'}
        </button>
      </div>
    </section>
  );
}
